import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Grid, Text } from '../../../ui';
import { LayoutBox } from '../../../components';
import { getAggregationColumnDefs, getPIFColumnDefs, mapLocationToPIFRow } from './RunReportsColumnDefs';
import { Rnd } from 'react-rnd';

const RunReports = ({ isOpen, onClose, aggregationLocations, onRowSelect, recentConfig, activePoliciesDate, viewMode }) => {
  const safeConfig = recentConfig ?? {};

  const [selectedRadio, setSelectedRadio] = useState('csr');
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);
  const [maxAggGridRef, setMaxAggGridRef] = useState(null);

  useEffect(() => {
    setMaxAggGridRef(null);
  }, [selectedRadio]);

  const radioOptions = [
    { label: 'CSR', value: 'csr' },
    { label: 'Floodzone', value: 'floodzone' },
    { label: 'CRZ', value: 'crz' },
    { label: 'PIF Report', value: 'pif' },
  ];

  useEffect(() => {
    if (!aggregationLocations || aggregationLocations.length === 0) {
      return;
    }

    if (selectedRadio === 'pif') {
      const pifCols = getPIFColumnDefs();
      const rows = aggregationLocations.map(mapLocationToPIFRow);
      setColumnDefs(pifCols);
      setRowData(rows);
      return;
    }

    const newColDefs = getAggregationColumnDefs(selectedRadio);

    const groupFieldMap = {
      csr: (loc) => loc.attributes?.csrId,
      floodzone: (loc) => loc.attributes?.femaFloodZone,
      crz: (loc) => loc.attributes?.crzId,
    };

    const rows = aggregationLocations.map((loc) => ({
      [selectedRadio]: groupFieldMap[selectedRadio](loc),
      coverage5mi: loc.aggregates?.coverage5mi ?? 0,
      coverage10mi: loc.aggregates?.coverage10mi ?? 0,
      coverage25mi: loc.aggregates?.coverage25mi ?? 0,
      id: loc.id ?? '',
    }));

    setColumnDefs(newColDefs);
    setRowData(rows);
  }, [selectedRadio, aggregationLocations]);

  useEffect(() => {
    if (selectedRadio === 'pif' || !aggregationLocations || aggregationLocations.length === 0) {
      return;
    }

    const highestLocation = aggregationLocations.reduce((max, loc) => {
      const maxValue = max.aggregates?.coverage5mi || 0;
      const locValue = loc.aggregates?.coverage5mi || 0;
      return locValue > maxValue ? loc : max;
    });

    if (highestLocation && onRowSelect) {
      onRowSelect([highestLocation], highestLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRadio, aggregationLocations]);

  const totalRow = useMemo(() => {
    if (!rowData || rowData.length === 0 || selectedRadio === 'pif') {
      return [];
    }
    const groups = new Map();

    rowData.forEach((r) => {
      const key = r[selectedRadio] ?? '__undefined__';
      const current = groups.get(key) || {
        coverage5mi: 0,
        coverage10mi: 0,
        coverage25mi: 0,
      };

      groups.set(key, {
        coverage5mi: Math.max(current.coverage5mi, r.coverage5mi || 0),
        coverage10mi: Math.max(current.coverage10mi, r.coverage10mi || 0),
        coverage25mi: Math.max(current.coverage25mi, r.coverage25mi || 0),
      });
    });

    let sum5 = 0;
    let sum10 = 0;
    let sum25 = 0;

    groups.forEach((vals) => {
      sum5 += vals.coverage5mi;
      sum10 += vals.coverage10mi;
      sum25 += vals.coverage25mi;
    });

    return [
      {
        [selectedRadio]: 'Total Result',
        coverage5mi: sum5,
        coverage10mi: sum10,
        coverage25mi: sum25,
      },
    ];
  }, [rowData, selectedRadio]);

  const gridOptions = useMemo(() => {
    if (selectedRadio === 'pif') {
      return {
        pivotMode: false,
        animateRows: true,
        suppressAggFuncInHeader: true,
        groupDefaultExpanded: 0,
        rowGroupPanelShow: 'never',
        suppressRowGroups: true,
      };
    }

    return {
      pivotMode: true,
      animateRows: true,
      suppressAggFuncInHeader: false,
      groupDefaultExpanded: 1,
    };
  }, [selectedRadio]);

  if (!isOpen) return null;

  const handleExportCSV = () => {
    if (maxAggGridRef && maxAggGridRef.api) {
      maxAggGridRef.api.exportDataAsCsv({
        fileName: `report_${selectedRadio}_${new Date().toISOString().split('T')[0]}.csv`,
        suppressQuotes: false, // Ensure all fields are quoted to preserve commas
        processCellCallback: (params) => {
          const raw = params.value;
          const colId = params.column?.getColId?.();

          // Return "null" for null/undefined/empty values instead of empty string or 0
          if (raw == null || raw === '') {
            return 'null';
          }

          if (colId === 'lat' || colId === 'lon') {
            const num = Number(raw);
            if (Number.isNaN(num)) return 'null';
            return `${num.toFixed(6)}`;
          }
          if (colId === 'bound_rate' || colId === 'calc_rate') {
            const num = Number(raw);
            if (Number.isNaN(num)) return 'null';

            const formatted = `$${num.toFixed(4)}`;

            return formatted;
          }

          // For strings, preserve commas (don't remove them) - ag-grid will quote the field
          if (typeof raw === 'string') {
            // Only remove $ from currency strings, but preserve commas
            return raw.replace(/\$/g, '');
          }

          return raw;
        },
      });
    }
  };

  const handleCellValueClicked = (params) => {
    if (selectedRadio === 'pif') {
      const rowIndex = params.node.rowIndex;
      const clickedLocation = aggregationLocations[rowIndex];

      if (clickedLocation) {
        onRowSelect([clickedLocation], clickedLocation);
      }
      return;
    }

    const fieldName = params.node.field;
    const rowNode = params.node;
    const groupValue = rowNode.key;

    if (!groupValue) return;

    const clickedColumnId = params.column.getColId();

    const coverageFieldMap = {
      coverage5mi: 'coverage5mi',
      coverage10mi: 'coverage10mi',
      coverage25mi: 'coverage25mi',
    };

    const coverageField = coverageFieldMap[clickedColumnId];

    const groupFieldMap = {
      csr: (loc) => loc.attributes?.csrId,
      floodzone: (loc) => loc.attributes?.femaFloodZone,
      crz: (loc) => loc.attributes?.crzId,
    };

    const locationsInGroup = aggregationLocations.filter((loc) => {
      if (!loc) return false;
      const fieldValueFromLoc = groupFieldMap[fieldName](loc);
      return fieldValueFromLoc === groupValue;
    });

    if (locationsInGroup.length === 0) return;

    let targetLocation;

    if (coverageField) {
      targetLocation = locationsInGroup.reduce((max, loc) => {
        const maxValue = max.aggregates?.[coverageField] || 0;
        const locValue = loc.aggregates?.[coverageField] || 0;
        return locValue > maxValue ? loc : max;
      });
    } else {
      targetLocation = locationsInGroup[0];
    }

    if (targetLocation) {
      onRowSelect([targetLocation], targetLocation);
    }
  };

  return (
    <Rnd
      default={{
        x: window.innerWidth * 0.04,
        y: window.innerHeight * 0.09,
        width: 1200,
        height: 750,
      }}
      minWidth={600}
      minHeight={450}
      maxWidth={window.innerWidth * 0.99}
      maxHeight={window.innerHeight * 0.99}
      bounds="parent"
      enableResizing={{
        top: true,
        right: true,
        bottom: true,
        left: true,
        topRight: true,
        bottomRight: true,
        bottomLeft: true,
        topLeft: true,
      }}
      style={{
        background: 'white',
        borderRadius: '10px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          gap: '0px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
          <Text sx={{ fontSize: '22px', fontWeight: 700 }}>Run Reports</Text>

          {safeConfig && viewMode !== 'pif' && (
            <>
              <Box
                sx={{
                  backgroundColor: '$primary',
                  border: '1px solid $primary',
                  borderRadius: '2px',
                  padding: '6px 14px',
                }}
              >
                <Text
                  sx={{
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(safeConfig.numberQuotes ?? safeConfig.quoteIds?.length ?? 0) + ' Quotes Included'}
                </Text>
              </Box>

              <Box
                sx={{
                  backgroundColor: '$primary',
                  border: '1px solid $primary',
                  borderRadius: '2px',
                  padding: '6px 14px',
                }}
              >
                <Text
                  sx={{
                    color: 'white',
                    fontSize: '15px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {`As of: ${activePoliciesDate ? activePoliciesDate.format('MM/DD/YYYY') : 'N/A'}`}
                </Text>
              </Box>
            </>
          )}
        </Box>

        <Button onPress={onClose} variant="text" color="$primary">
          Close
        </Button>
      </Box>

      <LayoutBox row gap="24px" alignItems="center" marginBottom="$1">
        {radioOptions.map((opt) => (
          <label key={opt.value} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="pivot-radio"
              value={opt.value}
              checked={selectedRadio === opt.value}
              onChange={() => setSelectedRadio(opt.value)}
              style={{ marginRight: 8 }}
            />
            {opt.label}
          </label>
        ))}
        <Button onPress={handleExportCSV} variant="contained" color="$primary" sx={{ marginLeft: 'auto' }}>
          Export CSV
        </Button>
      </LayoutBox>

      <Box sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
        <Grid
          style={{ width: '100%' }}
          key={selectedRadio}
          data={rowData}
          columnDefs={columnDefs}
          floatingFilter={false}
          shouldShowPagination={selectedRadio === 'pif'}
          setGridReference={setMaxAggGridRef}
          gridOptions={gridOptions}
          pinnedBottomRowData={totalRow}
          onCellClicked={handleCellValueClicked}
          pageSize={500}
        />
      </Box>
    </Rnd>
  );
};

export default RunReports;
